import React, { useEffect, useState, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import {
  editTrademarkNote,
  deleteTrademarkNote,
  saveNewNote,
  getEntityNotes,
} from '../RecordDetailsActions'
import NotesPanel from '../NotesPanel'
import RecordHistory from 'components/RecordHistory/RecordHistory'
import { Body } from 'components/Shell/Shell'
import * as api from '../../../requests'
import { SET_TRADEMARK_HISTORY, SET_RECORD_DETAILS } from '../RecordDetailsReducer'
import { Spinner } from 'components/Loaders/Loaders'
import GenericDetails from '../GenericDetails'
import { useRequestData } from '../../../Utilities/hooks'
import RecordDetailPageBackButton from '../RecordDetailPageBackButton'

const TrademarkRecordDetails = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { recordDetails, recordAmendmentHistory, recordHistoryList } = useSelector(
    (state) => state.record,
    shallowEqual
  )
  const RecordDetailsSettings = useRequestData((cb, ecb) =>
    api.GET_RECORD_DETAIL_SETTING_BY_TYPE('TrademarkRecordDetail', cb, ecb)
  )
  const [activeTabId, setActiveTabId] = useState('tab-1')
  const [loading, setLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  // const [thumbnailLoading, setThumbnailLoading] = useState(false)
  const tableRef = React.useRef(null)

  // KEEP THIS IN CASE WE BRING IT BACK User Story 48754: Removing Thumbnail on TM
  // const pngBytes = useRequestData(
  //   (cb, ecb) => {
  //     if (recordDetails.TrademarkThumbnailId) {
  //       setThumbnailLoading(true)
  //       api.GET_TRADEMARK_THUMBNAIL(
  //         recordDetails.TrademarkThumbnailId,
  //         (data) => {
  //           setThumbnailLoading(false)
  //           cb(data)
  //         },
  //         (err) => {
  //           setThumbnailLoading(false)
  //           ecb(err)
  //         }
  //       )
  //     }
  //   },
  //   [recordDetails.TrademarkThumbnailId],
  //   ''
  // )

  const trademarkNum = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  useEffect(() => {
    setLoading(true)
    api.FETCH_TRADEMARK_RECORD_DETAILS(
      trademarkNum,
      (record) => {
        dispatch({ type: SET_RECORD_DETAILS, record: record })
        dispatch({ type: SET_TRADEMARK_HISTORY, history: record.history })
        setLoading(false)
      },
      (err) => {
        console.error(err)
        setLoading(false)
      }
    )
  }, [trademarkNum])

  useEffect(() => {
    if (recordDetails.TrademarkRecordId)
      dispatch(getEntityNotes(810, recordDetails.TrademarkRecordId))
  }, [recordDetails.TrademarkRecordId])

  const saveTrademarkNote = (noteText) => {
    dispatch(saveNewNote(noteText, 810, recordDetails.TrademarkRecordId))
  }

  const updateTrademarkNote = (note) => {
    dispatch(editTrademarkNote(note, recordDetails.TrademarkRecordId))
  }

  const removeTrademarkNote = (noteId) => {
    dispatch(deleteTrademarkNote(noteId, recordDetails.TrademarkRecordId))
  }

  const refreshHistory = () => {
    setHistoryLoading(true)
    api.GET_TRADEMARK_FILING_HISTORY(
      trademarkNum,
      (history) => {
        dispatch({ type: SET_TRADEMARK_HISTORY, history: history })
        setHistoryLoading(false)
      },
      (err) => {
        console.error(err)
        setHistoryLoading(false)
      }
    )
  }

  return (
    <TabProvider activeTabId={activeTabId} onChange={(id) => setActiveTabId(id)}>
      <Body className="trademark-record-details">
        <div className="row header-row">
          <h1>Trademark Details</h1>
          <div className="tablist" role="tablist">
            <TabButton id="tab-1" forPanelId="panel-1" label="History" />
            <TabButton
              id="tab-2"
              forPanelId="panel-2"
              label="Notes"
              customCss={
                recordDetails['SeeNotesFlag'] === true
                  ? { backgroundColor: 'red !important', color: '#fff !important' }
                  : {}
              }
            />
          </div>
        </div>
        {loading && (
          <div style={{ height: '100%', width: '100%', textAlign: 'center', padding: '4rem' }}>
            <Spinner className="page-loader" themeColor="dark-neutral" />
          </div>
        )}

        {!loading && (
          <GenericDetails
            record={recordDetails}
            config={RecordDetailsSettings || {}}
            // KEEP THIS IN CASE WE BRING IT BACK User Story 48754: Removing Thumbnail on TM
            // beforeChildren={
            //   <DetailCard label={`Trademark Thumbnail`} className="detailsCard">
            //     {pngBytes.length > 0 && !thumbnailLoading ? (
            //       <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
            //         <img
            //           src={`data:image/png;base64,${pngBytes}`}
            //           style={{ maxHeight: 200, maxWidth: 200 }}
            //           alt="Trademark thumbnail"
            //         />
            //       </div>
            //     ) : thumbnailLoading ? (
            //       <div style={{ display: 'flex', justifyContent: 'center', padding: '15px' }}>
            //         <Spinner size="small" themeColor="dark-neutral" />
            //       </div>
            //     ) : (
            //       <div
            //         style={{
            //           textAlign: 'center',
            //           lineHeight: '1.2em',
            //           margin: 20,
            //           display: 'flex',
            //           flexDirection: 'column',
            //           justifyContent: 'center',
            //           alignItems: 'center',
            //           fontSize: '1.625em',
            //         }}
            //       >
            //         <div>
            //           <CrossHeavy width={80} color={'red'} />
            //         </div>
            //         <div>
            //           No
            //           <br />
            //           Thumbnail
            //           <br />
            //           Available
            //         </div>
            //       </div>
            //     )}
            //   </DetailCard>
            // }
          />
        )}

        <div ref={tableRef}>
          {historyLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '15px' }}>
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}
          {!loading && !historyLoading && (
            <TabPanel tabId="tab-1" id="panel-1">
              <RecordHistory
                history={{
                  amendments: recordAmendmentHistory,
                  history: recordHistoryList,
                  template: !!RecordDetailsSettings
                    ? RecordDetailsSettings.HistoryConfig.filter((t) => t.enabled)
                    : [],
                }}
                requiredVoidRight="TrademarkVoid"
                requiredEditRight={'EditTrademarkFiling'}
                setHistory={() => refreshHistory()}
                module="trademark"
              />
            </TabPanel>
          )}
          <TabPanel tabId="tab-2" id="panel-2">
            <NotesPanel
              saveNote={saveTrademarkNote}
              editNote={updateTrademarkNote}
              deleteNote={removeTrademarkNote}
            />
          </TabPanel>
        </div>
        <RecordDetailPageBackButton />
      </Body>
    </TabProvider>
  )
}

export default TrademarkRecordDetails

// these will need to be applied to config when they are added to the record.
// "TrademarkClass": {
//   "CardNumber": 1,
//   "Column": 0,
//   "Enabled": true,
//   "DisplayName": "Trademark Class",
//   "FieldType": 0,
//   "IsParent": false,
//   "Fields": null
// },
// "GoodsOrServices": {
//   "CardNumber": 1,
//   "Column": 0,
//   "Enabled": true,
//   "DisplayName": "Goods or Services",
//   "FieldType": 0,
//   "IsParent": false,
//   "Fields": null
// },

// "EmailNotifications": {
//   "CardNumber": 1,
//   "Column": 0,
//   "Enabled": true,
//   "DisplayName": "EmailNotifications",
//   "FieldType": 32,
//   // "EditApiCall": "/api/RegisteredAgentRecordDetail/UpdateRedFlag/{recordNum}/{value}",
//   // "EditPermissions": ["CreateNote"],
//   "IsParent": false,
//   "Fields": null
// },
